/**
 * The known Claude models an LLM Profile can run under, for the model picker
 * (spec 086). Hand-maintained — not fetched from any service — since only
 * `anthropic` is a creatable AI Provider type today (see `ai-providers`),
 * making a Claude-specific catalog sufficient rather than a constraint this
 * file needs to encode.
 *
 * Content mirrors Anthropic's own Active + Deprecated model listing at the
 * time this was written. Excludes Retired models (no longer callable at all)
 * and Mythos 5 (gated to a program this product does not participate in) —
 * spec 086 FR-009.
 */

export interface ClaudeModelEntry {
  /** The exact model identifier this entry sets the field to. */
  id: string;
  /** The human-readable label shown above `id` in the item. */
  displayName: string;
  /** Still callable, but no longer recommended for new profiles. */
  deprecated?: boolean;
}

export interface ClaudeModelTier {
  tier: string;
  /** Shown once beside the tier heading, not repeated per item. */
  efficiency: string;
  /** Alias row(s) first, a model's dated-snapshot row (if any) directly after. */
  models: readonly ClaudeModelEntry[];
}

export const CLAUDE_MODEL_CATALOG: readonly ClaudeModelTier[] = [
  {
    tier: 'Fable',
    efficiency: 'Most capable',
    models: [{ id: 'claude-fable-5', displayName: 'Claude Fable 5' }],
  },
  {
    tier: 'Opus',
    efficiency: 'Powerful',
    models: [
      { id: 'claude-opus-5', displayName: 'Claude Opus 5' },
      { id: 'claude-opus-4-8', displayName: 'Claude Opus 4.8' },
      { id: 'claude-opus-4-7', displayName: 'Claude Opus 4.7' },
      { id: 'claude-opus-4-6', displayName: 'Claude Opus 4.6' },
      { id: 'claude-opus-4-5', displayName: 'Claude Opus 4.5' },
      {
        id: 'claude-opus-4-5-20251101',
        displayName: 'Claude Opus 4.5 (2025-11-01)',
      },
      {
        id: 'claude-opus-4-1',
        displayName: 'Claude Opus 4.1',
        deprecated: true,
      },
      {
        id: 'claude-opus-4-1-20250805',
        displayName: 'Claude Opus 4.1 (2025-08-05)',
        deprecated: true,
      },
      {
        id: 'claude-opus-4-0',
        displayName: 'Claude Opus 4',
        deprecated: true,
      },
      {
        id: 'claude-opus-4-20250514',
        displayName: 'Claude Opus 4 (2025-05-14)',
        deprecated: true,
      },
    ],
  },
  {
    tier: 'Sonnet',
    efficiency: 'Balanced',
    models: [
      { id: 'claude-sonnet-5-5', displayName: 'Claude Sonnet 5.5' },
      { id: 'claude-sonnet-5', displayName: 'Claude Sonnet 5' },
      { id: 'claude-sonnet-4-6', displayName: 'Claude Sonnet 4.6' },
      { id: 'claude-sonnet-4-5', displayName: 'Claude Sonnet 4.5' },
      {
        id: 'claude-sonnet-4-5-20250929',
        displayName: 'Claude Sonnet 4.5 (2025-09-29)',
      },
      {
        id: 'claude-sonnet-4-0',
        displayName: 'Claude Sonnet 4',
        deprecated: true,
      },
      {
        id: 'claude-sonnet-4-20250514',
        displayName: 'Claude Sonnet 4 (2025-05-14)',
        deprecated: true,
      },
    ],
  },
  {
    tier: 'Haiku',
    efficiency: 'Fast',
    models: [
      { id: 'claude-haiku-5-5', displayName: 'Claude Haiku 5.5' },
      { id: 'claude-haiku-4-5', displayName: 'Claude Haiku 4.5' },
      {
        id: 'claude-haiku-4-5-20251001',
        displayName: 'Claude Haiku 4.5 (2025-10-01)',
      },
      {
        id: 'claude-3-haiku-20240307',
        displayName: 'Claude Haiku 3 (2024-03-07)',
        deprecated: true,
      },
    ],
  },
];

const BY_ID: ReadonlyMap<string, ClaudeModelEntry> = new Map(
  CLAUDE_MODEL_CATALOG.flatMap((tier) => tier.models).map((entry) => [
    entry.id,
    entry,
  ]),
);

/**
 * The friendly name of a known model ("Claude Opus 5"), or `undefined` for an
 * identifier the catalog does not hold — the caller shows that one as written.
 * One lookup for the admin's model picker and for everyone who reads a profile
 * (spec 214), so a profile reads by the name the admin chose it by.
 */
export function claudeModelName(id: string): string | undefined {
  return BY_ID.get(id)?.displayName;
}

const TIER_BY_ID: ReadonlyMap<string, string> = new Map(
  CLAUDE_MODEL_CATALOG.flatMap((tier) =>
    tier.models.map((entry) => [entry.id, tier.tier] as const),
  ),
);

/**
 * The family a known model belongs to ("Opus", "Sonnet"), or `undefined` for an
 * identifier the catalog does not hold. The AI models page marks each card with
 * it (spec 220), so cards on different families tell apart at a glance.
 */
export function claudeModelTier(id: string): string | undefined {
  return TIER_BY_ID.get(id);
}
